import { FileTextIcon, RotateCwIcon } from 'lucide-react'
import { motion, useReducedMotion } from 'motion/react'

import { cn } from '@/utils'
import { PaperSheet } from './PaperSheet'
import { SceneBadge } from './SceneBadge'

const LOOP_SECONDS = 5.5
// Appear, wait sideways, turn upright, come into focus, hold, fade out.
const TIMES = [0, 0.08, 0.24, 0.42, 0.5, 0.64, 0.92, 1]
const BLURRED = 'blur(3px)'
const SHARP = 'blur(0px)'

const loopTransition = {
  duration: LOOP_SECONDS,
  times: TIMES,
  repeat: Number.POSITIVE_INFINITY,
}

// A sideways, blurry page turns upright and comes into focus.
export const UprightPagesScene = () => {
  const reduceMotion = useReducedMotion() ?? false
  return (
    <div className="relative mx-auto flex h-full w-72 items-center justify-center">
      <TurningPage reduceMotion={reduceMotion} />
      <FocusCorners reduceMotion={reduceMotion} />
      {!reduceMotion && <RotateCue />}
      <PageBadge reduceMotion={reduceMotion} />
    </div>
  )
}

const TurningPage = ({ reduceMotion }: { reduceMotion: boolean }) => {
  const upright = { opacity: 1, rotate: 0, filter: SHARP }
  return (
    <motion.div
      initial={reduceMotion ? upright : { opacity: 0, rotate: -90 }}
      animate={
        reduceMotion
          ? upright
          : {
              opacity: [0, 1, 1, 1, 1, 1, 1, 0],
              rotate: [-90, -90, -90, 0, 0, 0, 0, 0],
              filter: [
                BLURRED,
                BLURRED,
                BLURRED,
                BLURRED,
                BLURRED,
                SHARP,
                SHARP,
                SHARP,
              ],
            }
      }
      transition={{ ...loopTransition, ease: 'easeInOut' }}
    >
      <PaperSheet label="Statement" icon={FileTextIcon} className="h-40 w-32" />
    </motion.div>
  )
}

const CORNERS = [
  'top-0 left-0 border-t-2 border-l-2 rounded-tl-md',
  'top-0 right-0 border-t-2 border-r-2 rounded-tr-md',
  'bottom-0 left-0 border-b-2 border-l-2 rounded-bl-md',
  'bottom-0 right-0 border-b-2 border-r-2 rounded-br-md',
]

const FocusCorners = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <motion.div
    className="absolute h-48 w-40"
    initial={{ opacity: reduceMotion ? 1 : 0 }}
    animate={
      reduceMotion
        ? { opacity: 1, scale: 1 }
        : {
            opacity: [0, 0, 0, 0, 1, 1, 1, 0],
            scale: [1.2, 1.2, 1.2, 1.2, 1.12, 1, 1, 1],
          }
    }
    transition={{ ...loopTransition, ease: 'easeOut' }}
  >
    {CORNERS.map((corner) => (
      <span
        key={corner}
        className={cn('absolute size-5 border-brand', corner)}
      />
    ))}
  </motion.div>
)

const RotateCue = () => (
  <motion.span
    className="absolute top-6 right-6 flex size-9 items-center justify-center rounded-full bg-white text-brand-deep shadow-md"
    initial={{ opacity: 0 }}
    animate={{
      opacity: [0, 1, 1, 0, 0, 0, 0, 0],
      rotate: [0, 0, 0, 90, 90, 90, 90, 90],
    }}
    transition={{ ...loopTransition, ease: 'easeInOut' }}
  >
    <RotateCwIcon aria-hidden className="size-4" />
  </motion.span>
)

const PageBadge = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <motion.span
    className="absolute top-1/2 left-1/2 mt-[-92px] ml-[52px]"
    initial={{ scale: reduceMotion ? 1 : 0 }}
    animate={reduceMotion ? { scale: 1 } : { scale: [0, 0, 1, 1, 0] }}
    transition={{
      duration: LOOP_SECONDS,
      times: [0, 0.64, 0.7, 0.92, 1],
      ease: 'backOut',
      repeat: Number.POSITIVE_INFINITY,
    }}
  >
    <SceneBadge kind="accepted" />
  </motion.span>
)
